import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui';
import { api } from '../../services/api';
import { useDeleteGenerationMutation } from '../../hooks/useApi';
import type { GenerationJob } from '../../types';

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatDateTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export interface GenerationJobItemProps {
  job: GenerationJob;
  onRemove: (jobId: string) => void;
  onCancel: (jobId: string) => void;
}

export const GenerationJobItem: React.FC<GenerationJobItemProps> = ({
  job,
  onRemove,
  onCancel,
}) => {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [now, setNow] = useState(Date.now());

  const deleteModalRef = useRef<HTMLDivElement>(null);
  const cancelModalRef = useRef<HTMLDivElement>(null);

  const deleteMutation = useDeleteGenerationMutation();
  const isDeleting = deleteMutation.isPending;

  // Focus trap helper: keeps Tab/Shift+Tab cycling within the modal
  const trapFocus = useCallback((e: KeyboardEvent, modalRef: React.RefObject<HTMLDivElement | null>) => {
    if (e.key !== 'Tab' || !modalRef.current) return;
    const focusable = modalRef.current.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  // Escape key + focus trap + scroll lock for Delete modal
  useEffect(() => {
    if (!showDeleteConfirm) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowDeleteConfirm(false);
        return;
      }
      trapFocus(e, deleteModalRef);
    };
    document.addEventListener('keydown', handleKeyDown);
    deleteModalRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDeleteConfirm, trapFocus]);

  // Escape key + focus trap + scroll lock for Cancel modal
  useEffect(() => {
    if (!showCancelConfirm) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowCancelConfirm(false);
        return;
      }
      trapFocus(e, cancelModalRef);
    };
    document.addEventListener('keydown', handleKeyDown);
    cancelModalRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showCancelConfirm, trapFocus]);

  // Tick every second for elapsed time while generating
  useEffect(() => {
    if (job.status !== 'generating') return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [job.status]);

  const elapsedTime = job.status === 'generating'
    ? Math.floor((now - job.startedAt) / 1000)
    : 0;

  const handlePreview = () => {
    if (job.sessionId) {
      navigate(`/preview/${job.sessionId}`);
    }
  };

  const handleDownload = () => {
    if (job.sessionId) {
      const url = api.getDownloadUrl(job.sessionId);
      window.open(url, '_blank');
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      // Delete from server if we have a sessionId (mutation auto-invalidates cache)
      if (job.sessionId) {
        await deleteMutation.mutateAsync(job.sessionId);
      }
      // Remove from local state
      onRemove(job.id);
    } catch (error) {
      console.error('Failed to delete generation:', error);
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const handleCancelClick = () => {
    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = () => {
    onCancel(job.id);
    setShowCancelConfirm(false);
  };

  const isGenerating = job.status === 'generating';
  const isComplete = job.status === 'complete';
  const isFailed = job.status === 'failed';

  const getStatusColor = () => {
    if (isComplete) return 'var(--color-success, #22c55e)';
    if (isFailed) return 'var(--color-error, #ef4444)';
    return 'var(--color-accent)';
  };

  const getStatusLabel = () => {
    if (isComplete) return 'Complete';
    if (isFailed) return 'Failed';
    return `${Math.round(job.progress)}%`;
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-4)',
        padding: 'var(--space-3) var(--space-4)',
        backgroundColor: 'var(--color-bg)',
        border: 'var(--border-thin)',
        borderLeft: `4px solid ${getStatusColor()}`,
      }}
    >
      {/* Business name and status */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-1)',
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: 'var(--text-sm)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {job.businessName?.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#0?39;/g, "'").replace(/&apos;/g, "'")}
          </span>
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-xs)',
              color: getStatusColor(),
              fontWeight: 500,
            }}
          >
            {getStatusLabel()}
          </span>
          {isGenerating && (
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-xs)',
                color: 'var(--color-fg)',
                fontWeight: 600,
              }}
            >
              {formatTime(elapsedTime)}
            </span>
          )}
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-muted)',
            }}
          >
            {formatDateTime(isComplete || isFailed ? (job.completedAt ?? job.startedAt) : job.startedAt)}
          </span>
        </div>

        {/* Progress bar */}
        {isGenerating && (
          <div
            role="progressbar"
            aria-valuenow={Math.round(job.progress)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Generation progress for ${job.businessName}: ${Math.round(job.progress)}% complete`}
            style={{
              width: '100%',
              height: '4px',
              backgroundColor: '#e5e7eb',
              border: 'var(--border-thin)',
            }}
          >
            <div
              style={{
                height: '100%',
                backgroundColor: 'var(--color-accent)',
                width: `${job.progress}%`,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        )}

        {/* Message or error */}
        {isGenerating && job.message && (
          <span
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-muted)',
              marginTop: 'var(--space-1)',
              display: 'block',
            }}
          >
            {job.message}
          </span>
        )}
        {isFailed && job.error && (
          <span
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-error, #ef4444)',
              marginTop: 'var(--space-1)',
              display: 'block',
            }}
          >
            {job.error}
          </span>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0 }}>
        {isComplete && (
          <>
            <Button size="sm" variant="secondary" onClick={handlePreview}>
              Preview
            </Button>
            <Button size="sm" variant="primary" onClick={handleDownload}>
              Download
            </Button>
          </>
        )}
        {isGenerating && (
          <Button size="sm" variant="ghost" onClick={handleCancelClick}>
            Cancel
          </Button>
        )}
        {(isComplete || isFailed) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDeleteClick}
            style={{ padding: 'var(--space-1)' }}
            aria-label="Delete"
            disabled={isDeleting}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </Button>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            ref={deleteModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-dialog-title"
            tabIndex={-1}
            style={{
              backgroundColor: 'var(--color-bg)',
              padding: 'var(--space-6)',
              border: 'var(--border-chunky)',
              boxShadow: 'var(--shadow-brutal-lg)',
              maxWidth: '400px',
              width: '90%',
              outline: 'none',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="delete-dialog-title" style={{ margin: '0 0 var(--space-3) 0', fontSize: 'var(--text-lg)', fontWeight: 600 }}>
              Delete Generation?
            </h3>
            <p style={{ margin: '0 0 var(--space-4) 0', color: 'var(--color-muted)', fontSize: 'var(--text-sm)' }}>
              Are you sure you want to delete "{job.businessName}"? This action cannot be undone and the generated files will be permanently removed.
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <Button size="sm" variant="ghost" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                style={{ backgroundColor: 'var(--color-error, #ef4444)' }}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Dialog */}
      {showCancelConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowCancelConfirm(false)}
        >
          <div
            ref={cancelModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-dialog-title"
            tabIndex={-1}
            style={{
              backgroundColor: 'var(--color-bg)',
              padding: 'var(--space-6)',
              border: 'var(--border-chunky)',
              boxShadow: 'var(--shadow-brutal-lg)',
              maxWidth: '400px',
              width: '90%',
              outline: 'none',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cancel-dialog-title" style={{ margin: '0 0 var(--space-3) 0', fontSize: 'var(--text-lg)', fontWeight: 600 }}>
              Cancel Generation?
            </h3>
            <p style={{ margin: '0 0 var(--space-4) 0', color: 'var(--color-muted)', fontSize: 'var(--text-sm)' }}>
              Are you sure you want to cancel the generation for "{job.businessName}"? The generation will be stopped and marked as failed.
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <Button size="sm" variant="ghost" onClick={() => setShowCancelConfirm(false)}>
                Keep Running
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={handleCancelConfirm}
                style={{ backgroundColor: 'var(--color-error, #ef4444)' }}
              >
                Cancel Generation
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerationJobItem;
